/* eslint-disable @typescript-eslint/no-namespace */
import type { MeasurementBoxProps } from '@/components/boxes/measurementBoxes/MeasurementBoxesController/MeasurementBoxes.controller.ts';
import type { ObjectBoxProps } from '@/components/boxes/objectBoxes/ObjectBoxes.controller';

import type { IHandlerDescriptor, IPointerController, IPointerHandler } from '@/components/viewPort/PointerController';
import type { PointerSyntheticEvent } from '@/components/viewPort/PointerSyntheticEvent';
import type { Point3D } from '../core/point3d';
import type {
  IDrawer,
  IPresentationChangeHandler,
  ITwoDViewModel,
  IUiThreeDPresentation,
  IUiThreeDPresentationContext,
  IUiTwoDPresentationContext,
  IWeightedGravityCenter
} from '@/engine/UIPresentation/Interfaces';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { ViewEntity, ViewPortInfo } from './models/viewEntity/view.entity.ts';
import type {
  ContextEvents,
  IImagesPortContext,
  ImagesMode,
  OrientationType,
  SlicerMode,
  SliceType
} from './modules/images/interfaces';
import type { ViewsService } from './Views.service';
import type { IKeyboardController } from '@/components/viewPort/KeyboardController';
import type { ViewPortTooltipViewModel } from './models/ViewPortTooltipViewModel';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm.ts';
import type { ImagesService } from '@/engine/app/modules/images/Images.service.ts';
import type { JSX } from 'react';
import type * as THREE from 'three';
import type { ViewController } from './View.controller.ts';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ICommandProcessor } from './modules/core/interfaces/Command.ts';
import type { IRemoveCommandsFactory } from './modules/indications/IndicationRemoveHighlighter.ts';
import { type reaction } from 'mobx';
import type { WFIndication } from '@/surgeries/cardiac/TTVR/configs/views/_wfIndications.config.ts';
import type { TwoDLayerController } from '@/components/viewPort/TwoDLayerController.ts';

export interface IThreeDObjectPresentationCreator {
  createPresentation: (
    bag: IPresentationFactoryBag,
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
}

export interface IImageThreeDObjectPresentationCreator {
  createPresentation: (
    bag: IImagePresentationFactoryBag,
    imagePort: IImagesPortContext,
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
}

export interface IThreeDMeasurementPresentationCreator {
  createPresentation: (
    bag: IPresentationFactoryBag,
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
}

export interface IImageThreeDMeasurementPresentationCreator {
  createPresentation: (
    bag: IImagePresentationFactoryBag,
    imagePort: IImagesPortContext,
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
}

export interface ITwoDObjectPresentationCreator {
  createPresentation: (
    bag: IPresentationFactoryBag,
    viewModel: IObjectViewModel
  ) => IUiTwoDPresentationContext | undefined;
}

export interface IImageTwoDObjectPresentationCreator {
  createPresentation: (
    bag: IImagePresentationFactoryBag,
    imagePort: IImagesPortContext,
    viewModel: IObjectViewModel
  ) => IUiTwoDPresentationContext | undefined;
}

export interface ITwoDMeasurementPresentationCreator {
  createPresentation: (
    bag: IPresentationFactoryBag,
    viewModel: IMeasurementViewModel
  ) => IUiTwoDPresentationContext | undefined;
}

export interface ITwoDMeasurementLabelPresentationCreator {
  createPresentation: (
    bag: IPresentationFactoryBag,
    viewModel: IMeasurementViewModel
  ) => IUiTwoDPresentationContext | undefined;
}

export interface IImageTwoDMeasurementPresentationCreator {
  createPresentation: (
    bag: IImagePresentationFactoryBag,
    imagePort: IImagesPortContext,
    viewModel: IMeasurementViewModel
  ) => IUiTwoDPresentationContext;
}

export interface IPresentationCreatorRegistrar {
  registerThreeDObjectCreator: (type: SceneItemKind, creator: IThreeDObjectPresentationCreator) => void;
  registerThreeDMeasurementCreator: (type: SceneItemKind, creator: IThreeDMeasurementPresentationCreator) => void;

  registerTwoDObjectCreator: (type: SceneItemKind, creator: ITwoDObjectPresentationCreator) => void;
  registerTwoDMeasurementCreator: (type: SceneItemKind, creator: ITwoDMeasurementPresentationCreator) => void;

  registerTwoDMeasurementLabelCreator: (type: SceneItemKind, creator: ITwoDMeasurementLabelPresentationCreator) => void;

  registerImageThreeDObjectCreator: (type: SceneItemKind, creator: IImageThreeDObjectPresentationCreator) => void;
  registerImageThreeDMeasurementCreator: (
    type: SceneItemKind,
    creator: IImageThreeDMeasurementPresentationCreator
  ) => void;

  registerImageTwoDObjectCreator: (type: SceneItemKind, creator: IImageTwoDObjectPresentationCreator) => void;
  registerImageTwoDMeasurementCreator: (type: SceneItemKind, creator: IImageTwoDMeasurementPresentationCreator) => void;
}

export interface IPresentationCreatorThreeDRegistry {
  resolveThreeDObjectCreator: (type: SceneItemKind) => IThreeDObjectPresentationCreator | undefined;
  resolveThreeDMeasurementCreator: (type: SceneItemKind) => IThreeDMeasurementPresentationCreator | undefined;

  resolveTwoDObjectCreator: (type: SceneItemKind) => ITwoDObjectPresentationCreator | undefined;
  resolveTwoDMeasurementCreator: (type: SceneItemKind) => ITwoDMeasurementPresentationCreator | undefined;

  resolveTwoDMeasurementLabelCreator: (type: SceneItemKind) => ITwoDMeasurementPresentationCreator | undefined;
}

export interface IPresentationCreatorImagesRegistry {
  resolveImageThreeDObjectCreator: (type: SceneItemKind) => IImageThreeDObjectPresentationCreator | undefined;
  resolveImageThreeDMeasurementCreator: (type: SceneItemKind) => IImageThreeDMeasurementPresentationCreator | undefined;

  resolveImageTwoDObjectCreator: (type: SceneItemKind) => IImageTwoDObjectPresentationCreator | undefined;
  resolveImageTwoDMeasurementCreator: (type: SceneItemKind) => IImageTwoDMeasurementPresentationCreator | undefined;
}

export interface IThreeDPresentationFactory {
  setRaycasterProvider: (raycasterProvider?: IRaycasterProvider) => void;
  createThreeDObjectPresentation: (
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
  createThreeDMeasurementPresentation: (
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
  createTwoDObjectPresentation: (viewModel: IObjectViewModel) => IUiTwoDPresentationContext | undefined;
  createTwoDMeasurementPresentation: (viewModel: IMeasurementViewModel) => IUiTwoDPresentationContext | undefined;
  createTwoDMeasurementLabelPresentation: (viewModel: IMeasurementViewModel) => IUiTwoDPresentationContext | undefined;
  createCenterLinePresentation: (
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
}

export interface IImagesPresentationFactory {
  setRaycasterProvider: (raycasterProvider?: IRaycasterProvider) => void;
  createThreeDObjectPresentation: (
    imagePort: IImagesPortContext,
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
  createThreeDMeasurementPresentation: (
    imagePort: IImagesPortContext,
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
  createTwoDObjectPresentation: (
    imagePort: IImagesPortContext,
    viewModel: IObjectViewModel
  ) => IUiTwoDPresentationContext | undefined;
  createTwoDMeasurementPresentation: (
    imagePort: IImagesPortContext,
    viewModel: IMeasurementViewModel
  ) => IUiTwoDPresentationContext | undefined;
  createCenterLinePresentation: (
    imagesPortContext: IImagesPortContext,
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
}

export interface IPresentationRegistrar {
  registerPresentations: (registrar: IPresentationCreatorRegistrar) => void;
}

export interface IPresentationFactoryBag {
  // WARNING: No side-effects during presentation creation!
  //   This bag is aggregator of all entities that are needed for presentation creation.
  //   All entities in this bag are intended for read-only usage - to be used by presentation creators.
  //   Do not add registries here and do not use them for side-effects during presentation creation.
  planningAlgorithm: PlanningAlgorithm;
  viewsService: ViewsService;
  viewController: ViewController;
  drawer: IDrawer;
  loggerService: ObservabilityLoggerService;
  commandProcessor: ICommandProcessor;
  removeIndicationCommandsFactory: IRemoveCommandsFactory;
  viewportController: IViewPortController;
  imagesService: ImagesService;
  raycasterProvider?: IRaycasterProvider;
  viewPortDescriptor: string;
}

export interface IImagePresentationFactoryBag extends Omit<IPresentationFactoryBag, 'raycastableRegistry'> {
  mainSliceType: SliceType;
}

export enum IntersectionKind {
  Point = 'Point'
}

export enum ViewPortKind {
  ImageSlice = 'ImageSlice',
  ThreeD = 'ThreeD'
}

export enum ViewPortType {
  Coronal = 'coronalView',
  Axial = 'axialView',
  Sagittal = 'sagittalView',
  ThreeD = '3dView'
}

// leaving type alias for consistency. Previously it was enum
export type ViewOrientationKind = string;

export enum MeasurementKind {
  Area = 'Area',
  WideArea = 'WideArea',
  AreaDiameter = 'AreaDiameter',
  Distance = 'Distance',
  VariableDistance = 'VariableDistance',
  Angle = 'Angle',
  Value = 'Value',
  Flag = 'Flag',
  Volume = 'Volume'
}

export enum ObjectsKind {
  CommissurePoints = 'CommissurePoints'
}

export enum OrientationPresetKind {
  Custom = 'Custom',
  Predefined = 'Predefined'
}

export enum TuneControlsKind {
  Select = 'Select',
  Stepper = 'Stepper',
  Switch = 'Switch',
  Reset = 'Reset'
}

export enum GroupKind {
  Collapsible = 'Collapsible'
}

export enum TuneControlsDisclaimerType {
  Device = 'Device',
  Morphing = 'Morphing'
}

export enum Transparency {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
  Opaque = 'Opaque'
}

export enum SceneItemKind {
  Mesh = 'Mesh',
  LineSegmentsMesh = 'LineSegmentsMesh',
  Polyline = 'Polyline',
  Point = 'Point',
  PointOnLine = 'PointOnLine',
  CuspPoint = 'CuspPoint',
  VariableDistancePoint = 'VariableDistancePoint',
  CommissurePoint = 'CommissurePoint',
  DistancePoint = 'DistancePoint',
  Line = 'Line',
  Plane = 'Plane',
  ShiftingPlane = 'ShiftingPlane',
  CustomShiftingPlane = 'CustomShiftingPlane',
  IndicatedDistance = 'IndicatedDistance',
  IndicatedAreaMeasurement = 'IndicatedAreaMeasurement',
  IndicatedSplineMeasurement = 'IndicatedSplineMeasurement',
  IndicatedAngle = 'IndicatedAngle',
  IndicatedAnnotation = 'IndicatedAnnotation',
  IndicatedSpline = 'IndicatedSpline',
  DistanceMeasurement = 'DistanceMeasurement',
  LabeledDistanceMeasurement = 'LabeledDistanceMeasurement',
  DiameterMeasurement = 'DiameterMeasurement',
  AreaMeasurement = 'AreaMeasurement',
  AreaIndication = 'AreaIndication',
  SplineAreaMeasurement = 'SplineAreaMeasurement',
  UnrestrictedSplineAreaMeasurement = 'UnrestrictedSplineAreaMeasurement',
  ValueMeasurement = 'ValueMeasurement',
  FlagMeasurement = 'FlagMeasurement',
  VolumeMeasurement = 'VolumeMeasurement',
  AngleMeasurement = 'AngleMeasurement',
  Spline = 'Spline',
  SplineCurve = 'SplineCurve',
  Sphere = 'Sphere'
}

export enum SceneItemAction {
  IndicationVisibilityChange = 'IndicationVisibilityChange',
  IndicationAdded = 'IndicationAdded',
  IndicationDeleted = 'IndicationDeleted',
  IndicationLabelEdited = 'IndicationLabelEdited'
}

export interface IImagesRoiConfig {
  descriptor: string;
  syncSliceDescriptor: SliceType;
  zoomFactor: number;
}

/**
 *  Generated TS Schema from JSON file
 */
export declare namespace JSONConfig {
  export interface Object {
    descriptor: string;
    label: string;
    color: string;
    kind: SceneItemKind;
    imagesMode?: ImagesMode;
    attachments?: Array<string>;
    isVertexColoring?: boolean;
    highlightColor?: string;
    selectionColor?: string;
    syncSliceDescriptor?: SliceType;
    surgeryExtension?: unknown;
    details?: unknown;
    imagesOverlayColor?: string;
    isFluoroOverlay?: boolean;
  }

  export interface CenterLine {
    descriptor: string;
    color: string;
    selectionColor: string;
    alongSliceDescriptor: SliceType;
  }

  export interface Orientation {
    descriptor: OrientationType;
    topVector: Point3D;
    directionVector: Point3D;
  }

  export interface ImageSlice {
    descriptor: SliceType;
    color: string;
    borderColor: string;
    isVisible: boolean;
    label: string;
    imageSlicerMode: SlicerMode;
  }

  export interface StaticImageSlice {
    descriptor: SliceType;
    sliceOrientation: string;
  }

  export interface ViewObject {
    descriptor: string;
    transparency: Transparency;
    isVisible: boolean;
    isContourVisible: boolean;
    isPickable?: boolean;
    isTunable?: boolean;
    isEditingActivated?: boolean;
    isClippable?: boolean;
    isSelected?: boolean;
  }

  export interface ViewMeasurement {
    descriptor: string;
    isSelected?: boolean;
    isVisible?: boolean;
    isTunable?: boolean;
    isEditingActivated?: boolean;
    isClippable?: boolean;
    isPickable?: boolean;
    zoomFactor?: number;
  }

  export interface ViewCenterLine {
    descriptor: string;
    isVisible?: boolean;
  }

  export interface ImageSliceViewPortDetails {
    sliceDescriptor: string;
  }

  export interface ThreeDViewPortDetails {
    defaultOrientationDescriptor: string;
    frontOrientationDescriptor: string;
  }

  export interface ViewPort {
    descriptor: string;
    kind: ViewPortKind;
    details: ImageSliceViewPortDetails | ThreeDViewPortDetails;
  }

  export interface MeasurementBox {
    kind: MeasurementKind;
    label: string;
    entries: Array<string>;
    groupDescriptor?: string;
    hasCriteria?: boolean;
    details?: Array<string>;
    order?: number;
  }

  export interface ObjectBox {
    kind: ObjectsKind;
    label: string;
    groupDescriptor?: string;
    entries: Array<{
      descriptor: string;
      label: string;
    }>;
    details?: Array<string>;
    order?: number;
  }

  export interface PlanningEntitiesGroup {
    label: string;
    descriptor: string;
    kind: GroupKind;
    order?: number;
    isExpanded?: boolean;
  }

  export interface Measurement {
    descriptor: string;
    attachments: Array<string>;
    label: string;
    color: string;
    kind: SceneItemKind;
    imagesMode: ImagesMode;
    highlightColor: string;
    selectionColor: string;
    clDescriptor: string;
    syncSliceDescriptor: SliceType;
    details?: unknown;
    isLabelColored?: boolean;
  }

  export interface TuneControl {
    descriptor: string | Array<string>;
    kind: TuneControlsKind;
    label: string;
    groupDescriptor?: string;
    units?: {
      value: string;
      subUnits?: {
        value: string;
        ratio: number;
      };
    };
    disclaimerType?: TuneControlsDisclaimerType;
    order?: number;
    locatableDescriptors?: Array<string>;
    hasReset?: boolean;
  }

  interface HelperMap {
    color_legend: ValueColorLegendHelper | RangeColorLegendHelper;
  }
  export interface Helper {
    descriptor: keyof HelperMap;
    details: HelperMap[keyof HelperMap];
  }

  export interface RangeColorLegendHelper {
    range: string;
  }

  export interface ValueColorLegendHelper {
    range: string;
    value: string;
  }

  export interface IVisibilityDependency {
    item: string;
    dependsOn: Array<string>;
  }

  export interface ISelectionDependency {
    item: string;
    dependsOn: Array<string>;
  }

  interface ImageStack {
    descriptor: string;
    label: string;
  }

  export interface IViewPortTooltip {
    descriptor: string;
    viewPort: string;
    text: string | Array<string>;
    placement: 'top' | 'bottom' | 'left' | 'right';
  }

  export interface ViewCameraSettings {
    cameraOrientationDescriptor: ViewOrientationKind;
    fittingFactor?: number;
  }

  export interface View {
    descriptor: string;
    label: string;
    sideLabel: string;
    imageStackDescriptor: string;
    objects: Array<ViewObject>;
    imageSlices: Array<ImageSlice>;
    viewPorts: Array<ViewPortType>;
    imagesRoiConfig?: IImagesRoiConfig;
    viewOrientationDescriptor: ViewOrientationKind;
    fluoroCurvePlaneDescriptor?: string;
    measurements: Array<ViewMeasurement>;
    centerLines: Array<ViewCenterLine>;
    measurementBoxes: Array<MeasurementBox>;
    objectBoxes: Array<ObjectBox>;
    planningEntitiesGroups?: Array<PlanningEntitiesGroup>;
    workflowIndications: Array<WFIndication>;
    tuneControls: Array<TuneControl>;
    orientationPresets: Array<OrientationPreset>;
    viewOptions: ViewOptionsStruct;
    visibilityDependencies: Array<IVisibilityDependency>;
    selectionDependencies: Array<ISelectionDependency>;
    helpers?: Array<Helper>;
    activeCenterLineDescriptor?: string | undefined;
    viewPortsTooltips?: Array<IViewPortTooltip>;
    availabilityDescriptor?: string;
    collapseImages?: boolean;
    cameraSettings?: ViewCameraSettings;
    descriptorsMapping?: Array<DescriptorMapItem>;
    descriptor2ViewOption?: Record<string, ViewOption>;
  }

  export interface Config {
    imageStacks: Array<ImageStack>;
    objects: Array<JSONConfig.Object>;
    viewPortOrientations: Array<Orientation>;
    imageSlices: Array<ImageSlice>;
    viewPorts: Array<ViewPort>;
    measurements: Array<Measurement>;
    centerLines: Array<CenterLine>;
    views: Array<View>;
  }

  export interface OrientationPreset {
    label: string;
    descriptor: string;
    kind?: OrientationPresetKind;
  }
}

export interface DescriptorMapItem {
  sourceDescriptor: string;
  targetDescriptor: string;
}

export interface IHighlightedSplineItem {
  highlightedSplineItem: SplineItems;
}

export interface ISplineDetails extends IHighlightedSplineItem {
  setHighlightedSplineItem: (item: SplineItems) => void;
}

export interface ViewOption {
  label: string;
  descriptors: Array<string>;
}

export type ViewOptionsStruct = Array<ViewOptionsGroup> | Array<ViewOption>;

export interface ViewOptionsGroup {
  id: string;
  label: string;
  children: Array<ViewOption>;
  disabledOptions?: Array<ViewOptionName>;
  isCollapsed?: boolean;
}

export enum ViewOptionName {
  Visibility = 'Visibility',
  Transparency = 'Transparency'
}

export enum SplineItems {
  None = 'None',
  Area = 'Area',
  Perimeter = 'Perimeter',
  PdDiameter = 'PdDiameter',
  MaxDiameter = 'MaxDiameter',
  MinDiameter = 'MinDiameter',
  ApDistance = 'ApDistance',
  SlDistance = 'SlDistance'
}

export interface IClonable<T extends IClonable<T>> {
  clone: () => T;
}

export interface IBaseViewModel<SurgerySpecificExtension = unknown> extends IClonable<IBaseViewModel> {
  descriptor: string;
  attachments: Array<string>;
  label: string;
  color: string;
  details: unknown;
  highlightColor: string;
  selectionColor: string;
  isVisible: boolean;
  isAvailable: boolean;
  isContourVisible: boolean;
  transparency: Transparency;
  isClippable: boolean;
  isPickable: boolean;
  isTunable: boolean;
  isHighlighted: boolean; // TODO: runtime property. Group similar runtime properties in a separate interface
  isSelected: boolean; // TODO: runtime property. Group similar runtime properties in a separate interface
  isBeingEdited: boolean; // TODO: runtime property. Group similar runtime properties in a separate interface
  kind: SceneItemKind;
  zoomFactor: number;
  phaseId: string | undefined;
  setVisibility: (visibility: boolean) => void;
  setAvailability: (availability: boolean) => void;
  setContourVisibility: (visibility: boolean) => void;
  setTransparency: (transparency: Transparency) => void;
  setDetails: (details: unknown) => void;
  setColor: (color: string) => void;
  setIsPickable: (isPickable: boolean) => void;
  setIsTunable: (isTunable: boolean) => void;
  setHighlighting: (highlighting: boolean) => void; // TODO: runtime property. Group similar runtime properties in a separate interface
  setSelection: (selected: boolean) => void; // TODO: runtime property. Group similar runtime properties in a separate interface
  setBeingEdited: (isBeingEdited: boolean) => void; // TODO: runtime property. Group similar runtime properties in a separate interface
  imported: boolean;
  isVertexColoring?: boolean;
  setLabel: (label: string) => void;
  surgeryExtension?: SurgerySpecificExtension;
  isLabelColored?: boolean;
}

export interface IObjectViewModel<SurgerySpecificExtension = unknown> extends IBaseViewModel<SurgerySpecificExtension> {
  syncSliceDescriptor?: SliceType;
  selectedIndex?: number | undefined; // TODO: runtime property. Group similar runtime properties in a separate interface
  isEnabled?: boolean;
  setIsEnabled?: (isEnabled: boolean) => void;
  setSelectedIndex?: (index: number | undefined) => void; // TODO: runtime property. Group similar runtime properties in a separate interface
  setHighlightColor?: (color: string) => void;
  setSelectionColor?: (color: string) => void;
  imagesMode?: ImagesMode;
  imagesOverlayColor?: string;
  isFluoroOverlay?: boolean;
}

export interface IMeasurementViewModel extends IBaseViewModel {
  syncSliceDescriptor: SliceType;
  imagesMode: ImagesMode;
  readyForSliding: boolean; // TODO: runtime property. Group similar runtime properties in a separate interface
  setReadyForSliding: (isReady: boolean) => void; // TODO: runtime property. Group similar runtime properties in a separate interface
  clDescriptor?: string;
}

export interface ICenterLineViewModel {
  descriptor: string;
  color: string;
  selectionColor: string;
  isHighlighted: boolean;
  isSelected: boolean;
  alongSliceDescriptor: SliceType;
  isVisible: boolean;
  setHighlighting: (highlighting: boolean) => void;
  setSelection: (selected: boolean) => void;
  setVisibility: (visible: boolean) => void;
}

export interface IImageSliceViewModel {
  descriptor: SliceType;
  label: string;
  color: string;
  borderColor: string;
  isVisible: boolean;
  imageSlicerMode: SlicerMode;
  setVisibility: (visibility: boolean) => void;
  setImageSlicerMode: (mode: SlicerMode) => void;
}

export type ISelectableItem = Pick<
  IBaseViewModel,
  'isSelected' | 'setSelection' | 'descriptor' | 'kind' | 'attachments'
> &
  Partial<Pick<IMeasurementViewModel, 'imagesMode' | 'clDescriptor'>>;

export interface Location {
  ptO: THREE.Vector3;
  dirZ: THREE.Vector3;
  dirY: THREE.Vector3;
  minCorner: THREE.Vector3;
  maxCorner: THREE.Vector3;
}

export interface IViewItemsViewModelController {
  setMeasurementVisibility: (descriptor: string, visible: boolean) => void;
  setMeasurementHighlighting: (descriptor: string, highlighted: boolean) => void;
  setViewModelVisibility: (descriptor: string, visible: boolean, attachedViewModel?: IBaseViewModel) => void;
  setViewModelSelection: (descriptor: string, selected: boolean, attachedViewModel?: IBaseViewModel) => void;
  setViewModelBeingEdited: (descriptor: string, isBeingEdited: boolean, useAttachments: boolean) => void;
  isViewModelBeingEdited: (descriptor: string, useAttachments: boolean) => boolean | undefined;
  locateMeasurementByDescriptor: (descriptor: string, excludedSlice?: SliceType) => void;
  locateIndicationByDescriptor: (descriptor: string, excludedSlice?: SliceType) => void;
  locatePointByDescriptor: (descriptor: string, excludedSlice?: SliceType) => void;
  locatePlaneByDescriptor: (descriptor: string, syncSlice: SliceType, excludedSlice?: SliceType) => void;
  syncMeasurementByDescriptor: (descriptor: string, point?: THREE.Vector3) => void;
  getActualViewModels: () => Array<IBaseViewModel>;
}

export interface IObjectViewModelStore {
  addViewModel: (vm: IObjectViewModel) => void;
  getByName: (name: string) => IObjectViewModel;
}

export interface IDefaultMeasurementsProps {
  isVisible: boolean;
  isHighlighted: boolean;
  isSelected: boolean;
  transparency: Transparency;
  isPickable: boolean;
  isTunable: boolean;
  isEditingActivated: boolean;
  isClippable: boolean;
  isContourVisible: boolean;
  highlightColor: string;
  selectionColor: string;
  color: string;
  zoomFactor: number;
}

export interface IViewFactory {
  createViewFromJSON: (viewDescriptor: JSONConfig.View) => ViewEntity;
}

export interface IViewFactoryProvider<T> {
  makeFactory: (config: T) => IViewFactory;
}

export interface ISceneItemDetailsFactory {
  createDetails: (kind: SceneItemKind) => unknown;
}

export interface IViewPortOrientation {
  topVector: THREE.Vector3;
  directionVector: THREE.Vector3;
}

export interface IProjectorApi {
  project: (threeDPoint: THREE.Vector3) => THREE.Vector2;
  unproject: (screenPoint: THREE.Vector2) => THREE.Vector3;
  getProjectionRay: (threeDPoint: THREE.Vector3) => THREE.Ray;
  getViewRay: (screenPoint: THREE.Vector2) => THREE.Ray;
}

export interface ICameraInfoApi {
  getCameraPosition: () => THREE.Vector3;
  getCameraRotation: () => THREE.Euler;
  getCameraDirection: () => THREE.Vector3;
  getCameraTop: () => THREE.Vector3;
  getCameraQuaternion: () => THREE.Quaternion;
  getCameraTransformation: () => THREE.Matrix4;
  getCameraClone: () => THREE.OrthographicCamera;
}

export interface ICameraManipulationApi {
  transformCamera: (mtx: THREE.Matrix4, shouldNotify?: boolean) => void;
  rotateCamera: (position: THREE.Vector3, direction: THREE.Vector3, angleRad: number, shouldNotify?: boolean) => void;
  panCamera: (vector: THREE.Vector3, shouldNotify?: boolean) => void;
  zoomCamera: (factor: number, ptOfInterest3d?: THREE.Vector3, shouldNotify?: boolean) => void;
  notifyCameraChanged: () => void;
}

export type OnCameraModifiedCallback = (cameraInfo: ICameraInfoApi) => void;

export interface CameraMemento {
  position: Array<number>;
  quaternion: Array<number>;
  left: number;
  right: number;
  yCenter: number;
  zoom: number;
}

export interface CameraFitParams {
  fittingFactor?: number;
  widthFittingFactor?: number;
  heightFittingFactor?: number;
  fitSideOverride?: 'width' | 'height';
}

export interface ICameraController extends IDisposable, IProjectorApi, ICameraInfoApi, ICameraManipulationApi {
  updateCanvas: (canvas: HTMLCanvasElement) => void;
  getCanvas: () => HTMLCanvasElement;
  onCameraModified: (callback: OnCameraModifiedCallback) => CallbackSubscription;
  accessCamera: <T>(accessor: (camera: THREE.OrthographicCamera) => T) => T;
  getFitCenterPoint: () => THREE.Vector3;
  getCameraZoomFactor: () => number;
  getCameraZoom: () => number;
  getFrameDimensions: () => THREE.Vector2;
  clampPointToFrame: (pt: THREE.Vector3, frameIndentFactor?: number) => THREE.Vector3 | undefined;
  setCameraOrientation: (orientation: IViewPortOrientation) => void;
  fitCamera: (target: THREE.Vector3, width: number, height: number, fitParams?: CameraFitParams) => void;
  fitCameraToObjects: (objects: Array<THREE.Mesh>, fitParams?: CameraFitParams) => void;
  fitCameraToPoints: (points: Array<THREE.Vector3>, fitParams?: CameraFitParams) => void;
  handleResize: () => void;
  centrifyCamera: (pt: THREE.Vector3, shouldResetZoom: boolean) => void;
  ensurePointIsVisible: (pt: THREE.Vector3, frameIndentFactor: number) => void;
  ensurePointsAreVisible: (pts: Array<THREE.Vector3>, frameIndentFactor: number) => void;
  synchronizeWith: (another: ICameraController) => void;
  resetZoom: () => void;
  getMemento: () => CameraMemento;
  setMemento: (memento: CameraMemento) => void;
}

export interface IIlluminator {
  getLight: () => THREE.DirectionalLight;
  getAmbientLight: () => THREE.AmbientLight;
  setOrientation: (topVector: THREE.Vector3, directionVector: THREE.Vector3) => void;
}

export interface IViewPortControllerFactory {
  createViewPortController: (
    descriptor: string,
    canvas: HTMLCanvasElement,
    canvasContainer: HTMLDivElement,
    bgColor: number
  ) => IViewPortController;
  updateViewPortController: (
    vpc: IViewPortController,
    descriptor: string,
    canvas: HTMLCanvasElement,
    canvasContainer: HTMLDivElement
  ) => void;
}

export interface ICameraInitializer extends IDisposable {
  initialize: () => void;
  fitCameraToBox: (
    minCorner: THREE.Vector3,
    maxCorner: THREE.Vector3,
    orientationTop: THREE.Vector3,
    orientationDir: THREE.Vector3,
    fittingFactor: number
  ) => void;
}

export interface IViewPortControllerStrategy {
  setViewRoi: (location: Location, zoomFactor: number) => void;
  synchronizeCamera: (anotherCamera: ICameraController) => void;
  setNoRotateCamera: (noRotate: boolean) => void;
  resetView: () => void;
}

export interface EnvInfo {
  productInfoBuildUIVersion: string | undefined;
  productInfoBuildAPIVersion: string | undefined;
}

export interface IViewPortController extends IDisposable {
  reInitialize: (canvas: HTMLCanvasElement, canvasContainer: HTMLDivElement, renderer: THREE.WebGLRenderer) => void;
  getTwoDLayerController: () => TwoDLayerController;
  setStrategy: (strategy: IViewPortControllerStrategy) => void;
  setViewPortManipulationApi: (viewPortManipulationApi?: IViewPortManipulationApi) => void;
  setContextStateApi: (contextStateApi?: IContextStateApi) => void;
  setClippingPlanes: (planes: Array<THREE.Plane>) => void;
  setViewRoi: (location: Location, zoomFactor: number) => void;
  rayCast: (raycastables: Set<IRaycastable>, point: THREE.Vector2) => Array<Intersection>;
  addTwoDObject: (twoDViewModel: ITwoDViewModel) => void;
  removeTwoDObject: (twoDViewModel: ITwoDViewModel) => void;
  addTooltip: (tooltip: ViewPortTooltipViewModel) => void;
  removeTooltip: (tooltip: ViewPortTooltipViewModel) => void;
  getCommonCenterPoint: () => THREE.Vector3;
  getCameraController: () => ICameraController;
  fitCameraToObjects: (fittingFactor?: number, objectsToFit?: Array<THREE.Mesh>) => void;
  getFitObjects: () => Array<THREE.Mesh>;
  synchronizeCamera: (anotherCamera: ICameraController) => void;
  setNoRotateCamera: (noRotate: boolean) => void;
  getSize: () => THREE.Vector2;
  setBackgroundColor: (bgColor: number) => void;
  setMaximized: (isMaximized: boolean) => void;
  isMaximized: () => boolean;
  addSceneObject: (
    object: THREE.Object3D,
    usedInFit: boolean,
    canBeClipped?: boolean,
    weightedGravityCenter?: IWeightedGravityCenter
  ) => void;
  removeSceneObject: (object: THREE.Object3D) => void;
  setupCamera: (orientation: IViewPortOrientation) => void;
  trackMaximizedChanged: (callback: (isMaximized: boolean) => void) => CallbackSubscription;
  addEventListener: (type: string, listener: EventListener) => IDisposable;
  addKeyboardEventListener: (type: string, listener: EventListener) => IDisposable;
  setPointerCapture: (pointerId: number) => void;
  setCursor: (cursor: string) => void;
  triggerEvent: (name: string, data: number) => void;
  setContextMenuState: (state: ContextEvents) => void;
  releasePointerCapture: (pointerId: number) => void;
  render: () => void;
  acquireRenderingLock: () => IDisposable;
  getScreenshot: () => Promise<string>;
  resize: () => void;
  setSizeExplicitly: (width: number, height: number) => void;
  resetSize: () => void;
  resetView: () => void;
  readonly width: number;
  readonly height: number;
}

export interface IViewPortManipulationApi {
  setViewPortController: (viewPortController: IViewPortController | undefined) => void;
}

export interface IContextStateApi {
  setContextState: (state: ContextEvents) => void;
}
export interface ITwoDLayerController extends IDisposable {
  twoDViewModels: Array<ITwoDViewModel>;
  tooltips: Array<ViewPortTooltipViewModel>;
  addViewModel: (model: ITwoDViewModel) => void;
  removeViewModel: (model: ITwoDViewModel) => void;
  addTooltip: (tooltip: ViewPortTooltipViewModel) => void;
  removeTooltip: (tooltip: ViewPortTooltipViewModel) => void;
  hideTooltip: (tooltip: ViewPortTooltipViewModel) => void;
}
export interface BoxesController {
  isDefaultAsync: (descriptor: string, approvedMeansDefault?: boolean, attachmentNumber?: number) => Promise<boolean>;
  resetToDefaultAsync: (descriptor: string, attachmentNumber?: number) => Promise<void>;
}
export interface IMeasurementsBoxesController extends BoxesController {
  getMeasurementBoxes: () => Array<MeasurementBoxProps>;
}

export interface IObjectBoxesController extends BoxesController {
  getObjectBoxes: () => Array<ObjectBoxProps>;
}

export interface INotificationsCenter {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  onWarning: (message: string) => void;
  onInfo: (message: string) => void;
}

export interface ICineloopInfo {
  readonly isCineloopActive: boolean;
}

export interface IRaycaster {
  rayCast: (point: THREE.Vector2, predicate?: (intersection: Intersection) => boolean) => Intersection | undefined;
}

export interface IRaycasterProvider {
  getRaycaster: () => IRaycaster;
}
export interface IViewPortManager extends IDisposable, IRaycasterProvider {
  getViewPortController: () => IViewPortController;
  setMeasurements: (viewModels: Array<IMeasurementViewModel>) => void;
  createImageSlices: (viewModels: Array<IImageSliceViewModel>) => void;
  setObjects: (viewModels: Array<IObjectViewModel>) => void;
  setCenterLines: (viewModels: Array<ICenterLineViewModel>) => void;
  setOrientation: (orientation: IViewPortOrientation) => void;
  setTooltips: (tooltips: Array<ViewPortTooltipViewModel>) => void;
  setPickingPossibility: (isPossible: boolean) => void;
  initCamera: () => void;
  accessPresentation: <T>(descriptor: string, accessor: (presentation: IUiThreeDPresentation | undefined) => T) => T;
  fitCameraToBox: (
    minCorner: THREE.Vector3,
    maxCorner: THREE.Vector3,
    orientationTop: THREE.Vector3,
    orientationDir: THREE.Vector3,
    fittingFactor: number
  ) => void;
  getCameraController: () => ICameraController;
  addPointerHandler: (handler: IPointerHandler) => IHandlerDescriptor;
  clearScene: () => void;
  getDescriptor: () => string;
}

export interface Intersection {
  point: THREE.Vector3;
  distance: number;
  descriptor: string;
  isClippable?: boolean;
  kind?: IntersectionKind;
}
export interface IRaycastable {
  rayCast: (origin: THREE.Vector3, direction: THREE.Vector3, doNotOptimize?: boolean) => Array<Intersection>;
}

export interface IPickable {
  getViewModel: () => IBaseViewModel | undefined;
  getPickedDistance: (event: PointerSyntheticEvent) => number | undefined;
  onPicked: (event: PointerSyntheticEvent, parent: IPointerController | undefined) => IPointerHandler | undefined;
  onUnPicked: (parent: IPointerController | undefined) => void;
}

export interface IPickableRegistry {
  getPickables: () => Array<IPickable>;
}

export interface IViewPortManagerStrategy extends IDisposable {
  getViewPortControllerStrategy: () => IViewPortControllerStrategy;
  setRaycasterProvider: (provider: IRaycasterProvider) => void;
  getDefaultPickables: () => Array<IPickable> | undefined;
  createDefaultCameraInitializer: () => ICameraInitializer;
  createThreeDObjectPresentation: (
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
  createCenterLinePresentation: (
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
  createThreeDMeasurementPresentation: (
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext | undefined;
  createTwoDObjectPresentation: (viewModel: IObjectViewModel) => IUiTwoDPresentationContext | undefined;
  createMeasurementTwoDPresentation: (viewModel: IMeasurementViewModel) => IUiTwoDPresentationContext | undefined;
  createImagePresentation: (
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler
  ) => IUiThreeDPresentationContext;
  createMeasurementLabel: (viewModel: IMeasurementViewModel) => IUiTwoDPresentationContext | undefined;
  createDependencyController: (viewModel: IMeasurementViewModel) => IDisposable | undefined;
  createViewHelpersPresentations?: (changeHandler: IPresentationChangeHandler) => Array<IUiThreeDPresentation>;
  createPointerHandlers: (pointerController: IPointerController) => void;
  createKeyboardHandlers: (keyboardController: IKeyboardController) => void;
}

export interface IViewPortManagerBuilder {
  buildStrategy: (
    viewController: ViewController,
    viewsService: ViewsService,
    viewPort: ViewPortInfo,
    controller: IViewPortController
  ) => IViewPortManagerStrategy;
}

export interface IResizeHandler {
  handleViewPortResize: (width: number, height: number) => void;
  resize: () => void;
}

export interface CommissurePointViewData {
  viewDescriptor: string;
  objects: Array<ObjectData>;
}

export interface ObjectData {
  descriptor: string;
  label: string;
  kind: SceneItemKind;
  phaseId: string | undefined;
  zoomFactor: number;
  color: string;
  attachments: Array<string>;
}

export interface IElementProps {
  children: Array<JSX.Element>;
}

export interface UseCase<Command = unknown, Result = unknown> {
  execute: (command: Command) => Result | Promise<Result>;
}

export interface IUserPreferencesController<T> {
  getStore: () => T;
  update: (store: T) => void;
}

export type SaveStateUpdateViewSubscription = (view: ViewEntity) => Parameters<typeof reaction>[0];

export interface ISugerySaveStateUpdateSubscriber {
  initSubcriptions: (callback: () => void) => void;
}
